import React, { useId } from 'react';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { SOFT_CARD_SX } from './PourStatusStrip';

interface CollapsibleSectionProps {
  /** The header text, with its count ("Targets (2 of 4 reached)"). */
  title: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  /** For scrolling to the section ("Show earlier breaches"). */
  id?: string;
}

/**
 * A section of the phone Location tab as a soft card: a 48px header in small
 * capitals that opens and closes it, closed by default (the page keeps the
 * state). The body mounts only while open.
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, open, onToggle, children, id }) => {
  const bodyId = useId();
  return (
    <Box id={id} sx={{ ...SOFT_CARD_SX, overflow: 'hidden', minWidth: 0 }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        sx={{
          width: '100%',
          minHeight: 52,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          textAlign: 'left',
        }}
      >
        <Typography
          component="h3"
          sx={{ fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#4a5056', minWidth: 0 }}
        >
          {title}
        </Typography>
        <ExpandMoreIcon
          aria-hidden
          sx={{ color: 'text.secondary', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box id={bodyId} sx={{ px: 2, pb: 2, minWidth: 0 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleSection;
